import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Spinner, EmptyState } from "@/components/common";
import { LevelBadge } from "@/components/LevelBadge";
import { AdminLayout, inp, lbl } from "@/pages/admin/adminShared";
import { formatDate } from "@/lib/format";
import { ListMusic, Eye } from "lucide-react";
import BackfillPanel from "@/pages/admin/BackfillPanel";
import PilotPanel from "@/pages/admin/PilotPanel";
import MemberPreviewPanel from "@/pages/admin/MemberPreviewPanel";

const ACT = { taught: "bg-[#C85A32] text-white", reviewed: "bg-[#FEF3C7] text-[#92600A]", danced: "bg-secondary text-[#5C4A42]" };

function Timeline({ data, side }) {
  if (!data?.length) return <EmptyState title="No history" subtitle={side === "old" ? "No legacy history for this class." : "No published/completed unified sessions yet."} />;
  return (
    <div className="space-y-3">
      {data.map((g) => (
        <div key={g.date} className="rounded-2xl border border-border bg-card p-3" data-testid={`${side}-date-${g.date}`}>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-bold text-[#B04B26]">{formatDate(g.date, { weekday: "short", year: true })}</p>
            {g.has_playlist && <span className="inline-flex items-center gap-1 text-xs text-[#6E5B51]"><ListMusic size={12} /> Playlist</span>}
          </div>
          <ul className="space-y-1.5">
            {g.dances.map((x) => (
              <li key={x.dance.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-secondary/40 px-2.5 py-1.5 text-sm">
                <span className="flex-1 font-semibold text-[#2D1E18]">{x.dance.name}</span>
                {(x.activities || []).map((a) => <span key={a} className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${ACT[a] || "bg-secondary"}`}>{a}</span>)}
                {x.tag && <span className="rounded-full bg-[#2D1E18] px-2 py-0.5 text-[10px] font-bold uppercase text-white">{x.tag}</span>}
                <LevelBadge level={x.dance.level} />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export default function UnifiedPreview() {
  const [classId, setClassId] = useState("");
  const { data: classes } = useQuery({ queryKey: ["classes-all"], queryFn: async () => (await api.get("/classes")).data });
  const { data: hist, isFetching: hf } = useQuery({ queryKey: ["prev-hist", classId], enabled: !!classId, queryFn: async () => (await api.get(`/admin/us/preview/class-history?class_id=${classId}`)).data });
  const { data: nd } = useQuery({ queryKey: ["prev-nd", classId], enabled: !!classId, queryFn: async () => (await api.get(`/admin/us/preview/new-dances?class_id=${classId}`)).data });

  return (
    <AdminLayout title="Unified Preview (admin)" subtitle="Read-only side-by-side of how member pages would look. Member pages are NOT changed; no global read-swap.">
      <div className="mb-4 flex items-center gap-2 rounded-2xl border border-[#F3C9A8] bg-[#FEF6EC] px-4 py-3 text-sm text-[#9A5B1E]" data-testid="preview-banner">
        <Eye size={18} /> Preview only. Drafts are never shown; New Dances counts only dances marked TAUGHT in completed sessions.
      </div>

      <BackfillPanel />

      <PilotPanel />

      <MemberPreviewPanel />

      <div className="max-w-md">
        <label className={lbl}>Class</label>
        <select className={inp} value={classId} onChange={(e) => setClassId(e.target.value)} data-testid="preview-class-select">
          <option value="">Select a class…</option>
          {(classes || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {!classId ? null : hf ? <Spinner /> : (
        <>
          <div className="mt-6">
            <h2 className="mb-2 font-display text-lg font-extrabold text-[#2D1E18]">Dashboard "New Dances" (class-scoped)</h2>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-2xl border border-border bg-card p-4" data-testid="nd-old">
                <p className="mb-2 text-xs font-bold uppercase text-[#6E5B51]">Legacy (current)</p>
                {(nd?.old || []).length ? <ul className="space-y-1 text-sm">{nd.old.map((d) => <li key={d.id} className="font-semibold text-[#2D1E18]">{d.name}</li>)}</ul> : <p className="text-sm text-[#6E5B51]">None</p>}
              </div>
              <div className="rounded-2xl border border-[#C85A32] bg-card p-4" data-testid="nd-new">
                <p className="mb-2 text-xs font-bold uppercase text-[#B04B26]">Unified (proposed — taught only)</p>
                {(nd?.new || []).length ? <ul className="space-y-1 text-sm">{nd.new.map((d) => <li key={d.id} className="font-semibold text-[#2D1E18]">{d.name}</li>)}</ul> : <p className="text-sm text-[#6E5B51]">None (no completed/taught sessions yet)</p>}
              </div>
            </div>
          </div>

          <div className="mt-8">
            <h2 className="mb-2 font-display text-lg font-extrabold text-[#2D1E18]">Class history</h2>
            <div className="grid gap-4 md:grid-cols-2">
              <div data-testid="hist-old"><p className="mb-2 text-xs font-bold uppercase text-[#6E5B51]">Legacy (current)</p><Timeline data={hist?.old} side="old" /></div>
              <div data-testid="hist-new"><p className="mb-2 text-xs font-bold uppercase text-[#B04B26]">Unified (proposed)</p><Timeline data={hist?.new} side="new" /></div>
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  );
}
